import { AppShell, Group, ActionIcon, Text, Anchor, Burger, Drawer, Stack, Button, Menu, Avatar, Badge } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBrain, IconBrandInstagram, IconBrandLinkedin, IconBrandYoutube, IconChevronDown, IconFileText, IconLayoutDashboard, IconLogout, IconSettings, IconUser, IconUsers } from '@tabler/icons-react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useAuthStore } from '@/stores/authStore';
import { useRoomStore } from '@/stores/roomStore';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [mobileMenuOpened, { toggle: toggleMobileMenu, close: closeMobileMenu }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams<{ code: string }>();
  const { user, logout, isAdmin, canCreateRoom, canManageTemplates } = useAuthStore();
  const currentRoom = useRoomStore((state) => state.currentRoom);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;
  const isLoginPage = location.pathname === '/';
  const isInRoom = location.pathname.startsWith('/room/');

  return (
    <AppShell
      header={{ height: 60 }}
      footer={!isInRoom ? { height: 80 } : undefined}
      padding={isInRoom || isLoginPage ? 0 : "md"}
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          {/* Logo */}
          <Group
            gap="sm"
            onClick={() => handleNavigation(isAuthenticated ? '/dashboard' : '/')}
            style={{ cursor: 'pointer' }}
          >
            <img
              src="/logo-senseup.jpeg"
              alt="SenseUp"
              style={{ height: 40, display: 'block' }}
            />
            <Text
              fw={700}
              style={{ fontFamily: 'Nunito, sans-serif', fontSize: '34px' }}
            >
              <span style={{ color: '#1971c2' }}>Intelliflow</span>{' '}
              <span style={{ color: '#1a1a1a' }}>Copilot</span>
            </Text>
          </Group>

          {/* Desktop Navigation */}
          {isAuthenticated && (
            <>
              <Group gap="lg" visibleFrom="sm">
                {/* Show room code and owner badge if in meeting room */}
                {isInRoom && code && (
                  <Group gap="xs">
                    <Text size="sm" fw={600}>Sala: {code.toUpperCase()}</Text>
                    {currentRoom?.isOwner && (
                      <Badge color="blue" variant="light" size="sm">
                        Owner
                      </Badge>
                    )}
                  </Group>
                )}
                <Anchor
                  onClick={() => handleNavigation('/dashboard')}
                  style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: 16 }}
                  c={location.pathname === '/dashboard' ? 'blue' : 'dark'}
                  fw={location.pathname === '/dashboard' ? 600 : 400}
                >
                  <Group gap={4}>
                    <IconLayoutDashboard size={16} />
                    Dashboard
                  </Group>
                </Anchor>

                {isAdmin() && (
                  <Anchor
                    onClick={() => handleNavigation('/admin')}
                    style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: 16 }}
                    c={location.pathname === '/admin' ? 'blue' : 'dark'}
                    fw={location.pathname === '/admin' ? 600 : 400}
                  >
                    <Group gap={4}>
                      <IconUsers size={16} />
                      Gerenciar Usuários
                    </Group>
                  </Anchor>
                )}

                {isAdmin() && (
                  <Anchor
                    onClick={() => handleNavigation('/llm-models')}
                    style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: 16 }}
                    c={location.pathname === '/llm-models' ? 'blue' : 'dark'}
                    fw={location.pathname === '/llm-models' ? 600 : 400}
                  >
                    <Group gap={4}>
                      <IconBrain size={16} />
                      Modelos LLM
                    </Group>
                  </Anchor>
                )}

                {canManageTemplates() && (
                  <Anchor
                    onClick={() => handleNavigation('/templates')}
                    style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: 16 }}
                    c={location.pathname === '/templates' ? 'blue' : 'dark'}
                    fw={location.pathname === '/templates' ? 600 : 400}
                  >
                    <Group gap={4}>
                      <IconFileText size={16} />
                      Templates
                    </Group>
                  </Anchor>
                )}

                {/* User Menu */}
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <Button
                      variant="subtle"
                      rightSection={<IconChevronDown size={16} />}
                      leftSection={<Avatar size={24} color="blue">{user?.name?.charAt(0).toUpperCase()}</Avatar>}
                    >
                      {user?.name}
                    </Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Label>Conta</Menu.Label>
                    <Menu.Item leftSection={<IconUser size={16} />}>
                      {user?.email}
                    </Menu.Item>
                    <Menu.Item leftSection={<IconSettings size={16} />} disabled>
                      Configurações
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconLogout size={16} />}
                      onClick={handleLogout}
                      color="red"
                    >
                      Sair
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>

              {/* Mobile Menu Button */}
              <Burger
                opened={mobileMenuOpened}
                onClick={toggleMobileMenu}
                hiddenFrom="sm"
                size="sm"
              />
            </>
          )}

          {/* Login page - no menu */}
          {!isAuthenticated && (
            <Text size="sm" c="dimmed" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Sistema de Apoio à Análise de Requisitos de Integração de Sistemas
            </Text>
          )}
        </Group>
      </AppShell.Header>

      {/* Mobile Drawer */}
      {isAuthenticated && (
        <Drawer
          opened={mobileMenuOpened}
          onClose={closeMobileMenu}
          title={
            <Group>
              <Avatar size={32} color="blue">{user?.name?.charAt(0).toUpperCase()}</Avatar>
              <div>
                <Text size="sm" fw={600}>{user?.name}</Text>
                <Text size="xs" c="dimmed">{user?.email}</Text>
              </div>
            </Group>
          }
          padding="md"
          size="xs"
        >
          <Stack gap="md">
            <Button
              variant={location.pathname === '/dashboard' ? 'filled' : 'subtle'}
              onClick={() => handleNavigation('/dashboard')}
              fullWidth
              justify="flex-start"
              leftSection={<IconLayoutDashboard size={16} />}
            >
              Dashboard
            </Button>

            {isAdmin() && (
              <Button
                variant={location.pathname === '/admin' ? 'filled' : 'subtle'}
                onClick={() => handleNavigation('/admin')}
                fullWidth
                justify="flex-start"
                leftSection={<IconUsers size={16} />}
              >
                Gerenciar Usuários
              </Button>
            )}

            {isAdmin() && (
              <Button
                variant={location.pathname === '/llm-models' ? 'filled' : 'subtle'}
                onClick={() => handleNavigation('/llm-models')}
                fullWidth
                justify="flex-start"
                leftSection={<IconBrain size={16} />}
              >
                Modelos LLM
              </Button>
            )}

            {canManageTemplates() && (
              <Button
                variant={location.pathname === '/templates' ? 'filled' : 'subtle'}
                onClick={() => handleNavigation('/templates')}
                fullWidth
                justify="flex-start"
                leftSection={<IconFileText size={16} />}
              >
                Templates
              </Button>
            )}

            <Button
              variant="subtle"
              color="gray"
              fullWidth
              justify="flex-start"
              disabled
            >
              Configurações
            </Button>

            <Button
              variant="subtle"
              color="red"
              onClick={handleLogout}
              fullWidth
              justify="flex-start"
              leftSection={<IconLogout size={16} />}
            >
              Sair
            </Button>
          </Stack>
        </Drawer>
      )}

      {/* Main Content */}
      <AppShell.Main>
        {children}
      </AppShell.Main>

      {/* Footer */}
      {!isInRoom && (
      <AppShell.Footer>
        <Group
          justify="space-between"
          align="center"
          wrap="wrap"
          gap="md"
          style={{
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            padding: '1rem 1.5rem',
            fontFamily: 'Open Sans, sans-serif',
            height: '100%',
          }}
        >
          {/* Contact Info */}
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              Contato: (27) 99256-9897
            </Text>
            <Anchor
              href="mailto:sales@senseup.tech"
              size="sm"
              c="blue.5"
              underline="hover"
            >
              sales@senseup.tech
            </Anchor>
          </Stack>

          {/* Social Media */}
          <Group gap="md">
            <ActionIcon
              component="a"
              href="https://www.instagram.com/senseup.tech/"
              target="_blank"
              variant="subtle"
              color="blue.5"
              size="lg"
              aria-label="Instagram"
            >
              <IconBrandInstagram size={20} />
            </ActionIcon>
            <ActionIcon
              component="a"
              href="https://www.linkedin.com/company/senseup-tech/"
              target="_blank"
              variant="subtle"
              color="blue.5"
              size="lg"
              aria-label="LinkedIn"
            >
              <IconBrandLinkedin size={20} />
            </ActionIcon>
            <ActionIcon
              component="a"
              href="https://www.youtube.com/@SenseUpTech"
              target="_blank"
              variant="subtle"
              color="blue.5"
              size="lg"
              aria-label="YouTube"
            >
              <IconBrandYoutube size={20} />
            </ActionIcon>
          </Group>

          {/* Copyright */}
          <Text size="sm" c="dimmed">
            SenseUp Tech © 2025
          </Text>
        </Group>
      </AppShell.Footer>
      )}
    </AppShell>
  );
}
